import type { RootStore } from 'logic';
import { RouteNames } from 'logic/routes';
import { getCanonicalQueueRouteStepId } from 'logic/ui-active-session';
import { getCanonicalAssetsRouteBucketId, normalizeAssetRouteBucketId } from 'logic/ui-assets';
import {
  getProjectSearchScrollIntentStorageKey,
  PROJECT_SEARCH_RESET_EVENT,
} from 'logic/project-search-scroll';
import type { ProjectTab } from './ProjectTabs';

export const getProjectTabFromRoute = (
  routeName: string | undefined,
  routeParams?: Record<string, unknown>,
): ProjectTab | null => {
  const sourceId = routeParams?.sourceId as string | undefined;
  if (sourceId && routeName === RouteNames.SESSION_HOME_SOURCE) {
    return null;
  }

  switch (routeName) {
    case RouteNames.SESSION_HOME:
    case RouteNames.SESSION_SEARCH:
    case RouteNames.SESSION_HOME_SOURCE:
    case RouteNames.SESSION_FIND:
      return 'home';
    case RouteNames.SESSION_QUEUE:
    case RouteNames.SESSION_QUEUE_LEGACY:
    case RouteNames.SESSION_STEP:
    case RouteNames.SESSION_LEGACY:
    case RouteNames.CARD:
    case RouteNames.CARD_LEGACY:
      return 'queue';
    case RouteNames.SESSION_LIBRARY:
    case RouteNames.SESSION_LIBRARY_LEGACY:
      return 'library';
    case RouteNames.SESSION_ASSETS:
      return 'assets';
    case RouteNames.SESSION_WHOLE:
      return 'whole';
    case RouteNames.SESSION_DASHBOARD:
    case RouteNames.SESSION_DASHBOARD_LEGACY:
    case RouteNames.SESSION_MILESTONES:
    case RouteNames.SESSION_MILESTONES_LEGACY:
      return 'status';
    case RouteNames.SESSION_NOTES:
    case RouteNames.SESSION_NOTES_LEGACY:
      return 'notes';
    default:
      return 'home';
  }
};

export const getRouteFromProjectTab = (tab: ProjectTab): RouteNames => {
  switch (tab) {
    case 'home':
      return RouteNames.SESSION_SEARCH;
    case 'queue':
      return RouteNames.SESSION_QUEUE;
    case 'library':
      return RouteNames.SESSION_LIBRARY;
    case 'assets':
      return RouteNames.SESSION_ASSETS;
    case 'whole':
      return RouteNames.SESSION_WHOLE;
    case 'status':
      return RouteNames.SESSION_DASHBOARD;
    case 'notes':
      return RouteNames.SESSION_NOTES;
    default:
      return RouteNames.SESSION_SEARCH;
  }
};

export const getProjectTabRouteTarget = ({
  tab,
  sessionId,
  sourceId,
  selectedQueueTab,
  selectedStepTabId,
  sessionSteps,
  getStepCardCount,
  activeAssetStatusFilter,
  assetsRouteStepId,
  sessionStepIds,
}: {
  tab: ProjectTab;
  sessionId: string;
  sourceId?: string;
  selectedQueueTab?: Parameters<typeof getCanonicalQueueRouteStepId>[0]['selectedQueueTab'];
  selectedStepTabId?: Parameters<typeof getCanonicalQueueRouteStepId>[0]['selectedStepTabId'];
  sessionSteps?: Parameters<typeof getCanonicalQueueRouteStepId>[0]['sessionSteps'];
  getStepCardCount?: Parameters<typeof getCanonicalQueueRouteStepId>[0]['getStepCardCount'];
  activeAssetStatusFilter?: string | null;
  assetsRouteStepId?: string;
  sessionStepIds?: string[];
}): {
  routeName: RouteNames;
  params: { sessionId: string; sourceId?: string; stepId?: string };
} => {
  let routeName = getRouteFromProjectTab(tab);
  const params: { sessionId: string; sourceId?: string; stepId?: string } = { sessionId };

  if (tab === 'home' && sourceId) {
    routeName = RouteNames.SESSION_HOME_SOURCE;
    params.sourceId = sourceId;
  } else if (tab === 'queue') {
    params.stepId = getCanonicalQueueRouteStepId({
      sessionId,
      selectedQueueTab: selectedQueueTab!,
      selectedStepTabId: selectedStepTabId ?? null,
      sessionSteps: sessionSteps ?? [],
      getStepCardCount,
    });
  } else if (tab === 'library' && sourceId) {
    params.sourceId = sourceId;
  } else if (tab === 'assets') {
    params.stepId = assetsRouteStepId
      ? normalizeAssetRouteBucketId(assetsRouteStepId, sessionStepIds, !!sessionStepIds)
      : getCanonicalAssetsRouteBucketId({
          sessionId,
          activeStatusFilter: activeAssetStatusFilter ?? null,
          sessionStepIds,
          validateCustomStepIds: !!sessionStepIds,
        });
  }

  return { routeName, params };
};

export const navigateToProjectTab = (
  rootStore: RootStore,
  tab: ProjectTab,
  sourceId?: string,
): void => {
  const { routerStore, sessionsStore, uiActiveSessionStore, uiAssetsStore } = rootStore;
  const sessionId =
    sessionsStore.currentSessionId ||
    (routerStore.routerState?.params?.sessionId as string | undefined);

  if (!sessionId) {
    return;
  }

  if (tab === 'home' && !sourceId) {
    try {
      sessionStorage.setItem(getProjectSearchScrollIntentStorageKey(sessionId), 'reset');
    } catch (error) {
      console.warn('Failed to store Search reset intent:', error);
    }
    window.dispatchEvent(new CustomEvent(PROJECT_SEARCH_RESET_EVENT));
  }

  const target = getProjectTabRouteTarget({
    tab,
    sessionId,
    sourceId,
    selectedQueueTab: uiActiveSessionStore.selectedQueueTab,
    selectedStepTabId: uiActiveSessionStore.selectedStepTabId,
    sessionSteps: sessionsStore.currentSession?.sortedSteps ?? [],
    getStepCardCount: (stepId: string) =>
      sessionsStore.currentSession?.getCardIdsByStepId(stepId).length ?? 0,
    activeAssetStatusFilter: uiAssetsStore.activeStatusFilter,
    assetsRouteStepId:
      routerStore.getCurrentRoute()?.name === RouteNames.SESSION_ASSETS
        ? (routerStore.routerState.params.stepId as string | undefined)
        : undefined,
    sessionStepIds: sessionsStore.currentSession?.sortedSteps.map((step) => step.id),
  });

  routerStore.goTo(target.routeName, {
    params: target.params,
  });
};
